let audioContext;
let toneModulePromise;
let toneEngine;

const vibrateIfSupported = (pattern) => {
  try {
    if (typeof navigator === 'undefined' || typeof navigator.vibrate !== 'function') {
      return;
    }

    navigator.vibrate(pattern);
  } catch (error) {
    console.error(error);
  }
};

const getAudioContext = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const Context = window.AudioContext || window.webkitAudioContext;
  if (!Context) {
    return null;
  }

  if (!audioContext) {
    audioContext = new Context();
  }

  return audioContext;
};

const playTone = (frequency, duration, gain = 0.045) => {
  try {
    const context = getAudioContext();
    if (!context) {
      return;
    }

    const now = context.currentTime;
    const oscillator = context.createOscillator();
    const gainNode = context.createGain();

    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(frequency, now);
    gainNode.gain.setValueAtTime(0.0001, now);
    gainNode.gain.exponentialRampToValueAtTime(gain, now + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, now + duration);

    oscillator.connect(gainNode);
    gainNode.connect(context.destination);

    oscillator.start(now);
    oscillator.stop(now + duration);
  } catch (error) {
    console.error(error);
  }
};

const playToneAt = (frequency, duration, startDelayMs = 0, gain = 0.045, type = 'sine') => {
  try {
    const context = getAudioContext();
    if (!context) {
      return;
    }

    const now = context.currentTime + Math.max(0, startDelayMs) / 1000;
    const oscillator = context.createOscillator();
    const gainNode = context.createGain();

    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, now);
    gainNode.gain.setValueAtTime(0.0001, now);
    gainNode.gain.exponentialRampToValueAtTime(gain, now + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, now + duration);

    oscillator.connect(gainNode);
    gainNode.connect(context.destination);

    oscillator.start(now);
    oscillator.stop(now + duration);
  } catch (error) {
    console.error(error);
  }
};

const getToneEngine = async () => {
  if (typeof window === 'undefined') {
    return null;
  }

  if (!toneModulePromise) {
    toneModulePromise = import('tone').catch((error) => {
      console.error(error);
      return null;
    });
  }

  const Tone = await toneModulePromise;
  if (!Tone) {
    return null;
  }

  try {
    await Tone.start();
  } catch (error) {
    // Audio context start can fail without a user gesture; ignore gracefully.
    return null;
  }

  if (!toneEngine) {
    const limiter = new Tone.Limiter(-4).toDestination();
    const epicChorus = new Tone.Chorus(2.2, 2.8, 0.24).start();
    epicChorus.connect(limiter);
    const legendaryHall = new Tone.Freeverb({ roomSize: 0.92, dampening: 2800, wet: 0.28 });
    legendaryHall.connect(limiter);

    const rareSynth = new Tone.FMSynth({
      harmonicity: 1.8,
      modulationIndex: 7,
      oscillator: { type: 'triangle' },
      envelope: { attack: 0.01, decay: 0.18, sustain: 0.08, release: 0.28 },
      modulation: { type: 'sine' },
      modulationEnvelope: { attack: 0.01, decay: 0.2, sustain: 0.04, release: 0.25 },
    }).connect(new Tone.Gain(0.18).connect(limiter));

    const epicSynth = new Tone.PolySynth(Tone.FMSynth, {
      harmonicity: 1.6,
      modulationIndex: 9,
      oscillator: { type: 'triangle' },
      envelope: { attack: 0.012, decay: 0.24, sustain: 0.12, release: 0.65 },
      modulation: { type: 'sine' },
      modulationEnvelope: { attack: 0.01, decay: 0.2, sustain: 0.1, release: 0.5 },
    }).connect(new Tone.Gain(0.12).connect(epicChorus));

    const legendarySynth = new Tone.MetalSynth({
      frequency: 190,
      envelope: { attack: 0.001, decay: 0.75, release: 1.2 },
      harmonicity: 9.5,
      modulationIndex: 26,
      resonance: 4200,
      octaves: 2.1,
    }).connect(new Tone.Gain(0.13).connect(legendaryHall));

    const legendaryTrail = new Tone.PolySynth(Tone.AMSynth, {
      harmonicity: 1.4,
      oscillator: { type: 'triangle' },
      envelope: { attack: 0.018, decay: 0.32, sustain: 0.18, release: 1.2 },
      modulation: { type: 'sine' },
      modulationEnvelope: { attack: 0.01, decay: 0.2, sustain: 0.1, release: 0.75 },
    }).connect(new Tone.Gain(0.1).connect(legendaryHall));

    const legendarySub = new Tone.MonoSynth({
      oscillator: { type: 'sine' },
      filter: { Q: 1.2, type: 'lowpass', rolloff: -24 },
      envelope: { attack: 0.01, decay: 0.22, sustain: 0.08, release: 0.55 },
      filterEnvelope: {
        attack: 0.001,
        decay: 0.2,
        sustain: 0.02,
        release: 0.35,
        baseFrequency: 120,
        octaves: 2.4,
      },
    }).connect(new Tone.Gain(0.09).connect(limiter));

    toneEngine = {
      Tone,
      rareSynth,
      epicSynth,
      legendarySynth,
      legendaryTrail,
      legendarySub,
    };
  }

  return toneEngine;
};

export const playTilePlaceSound = () => {
  playTone(540, 0.07, 0.04);
};

export const playSubmitSuccessSound = () => {
  playTone(720, 0.08, 0.05);
  setTimeout(() => playTone(900, 0.09, 0.045), 55);
};

export const playDiscardModeEnterSound = () => {
  playTone(450, 0.06, 0.04);
  setTimeout(() => playTone(560, 0.07, 0.038), 45);
};

export const playDiscardMarkSound = () => {
  playTone(640, 0.045, 0.033);
};

export const playDiscardConfirmSound = () => {
  playToneAt(380, 0.12, 0, 0.045, 'triangle');
  playToneAt(300, 0.1, 55, 0.04, 'triangle');
  playToneAt(740, 0.07, 130, 0.05, 'sine');
  playToneAt(920, 0.08, 210, 0.047, 'sine');
};

export const playShopRareRevealSound = (startDelayMs = 0) => {
  void (async () => {
    const engine = await getToneEngine();
    if (!engine) {
      return;
    }

    const when = engine.Tone.now() + Math.max(0, startDelayMs) / 1000;
    engine.rareSynth.triggerAttackRelease('E5', '16n', when);
    engine.rareSynth.triggerAttackRelease('B5', '8n', when + 0.085);
  })();
};

export const playShopEpicRevealSound = (startDelayMs = 0) => {
  void (async () => {
    const engine = await getToneEngine();
    if (!engine) {
      return;
    }

    const when = engine.Tone.now() + Math.max(0, startDelayMs) / 1000;
    engine.epicSynth.triggerAttackRelease(['E4', 'B4'], '8n', when);
    engine.epicSynth.triggerAttackRelease(['G4', 'D5', 'B5'], '8n', when + 0.11);
    engine.epicSynth.triggerAttackRelease(['B4', 'E5', 'G5'], '4n', when + 0.24);
  })();
};

export const playShopLegendaryRevealSound = (startDelayMs = 0) => {
  void (async () => {
    const engine = await getToneEngine();
    if (!engine) {
      return;
    }

    const when = engine.Tone.now() + Math.max(0, startDelayMs) / 1000;
    engine.legendarySub.triggerAttackRelease('E2', '8n', when);
    engine.legendarySynth.triggerAttackRelease('16n', when + 0.01);
    engine.legendaryTrail.triggerAttackRelease(['G4', 'B4', 'D5'], '8n', when + 0.06);
    engine.legendaryTrail.triggerAttackRelease(['A4', 'C5', 'E5'], '8n', when + 0.17);
    engine.legendaryTrail.triggerAttackRelease(['B4', 'D5', 'G5'], '2n', when + 0.32);
  })();
};

export const triggerDiscardModeEnterHaptic = () => {
  vibrateIfSupported(12);
};

export const triggerDiscardMarkHaptic = () => {
  vibrateIfSupported(8);
};

export const triggerDiscardConfirmHaptic = () => {
  vibrateIfSupported([12, 30, 18]);
};

export const playModifierPlaceSound = () => {
  playToneAt(520, 0.09, 0, 0.048, 'triangle');
  playToneAt(680, 0.10, 70, 0.046, 'sine');
  playToneAt(880, 0.13, 155, 0.052, 'sine');
};

export const triggerModifierPlaceHaptic = () => {
  vibrateIfSupported([10, 25, 14]);
};